import { useMemo } from 'react';
import { useSavingPlanStore } from 'stores/saving-plan';
import { PdProductItem } from '../../interfaces/line-item';

/**
 * Formats a savings plan item with proper display name and empty image
 */
const formatSavingsPlanItem = <T extends PdProductItem>(item: T, planName: string): T => {
  const formattedName = planName === 'premium' ? 'Savings Plan Premium' : 'Savings Plan Clever';

  return {
    ...item,
    productTitle: formattedName,
    variantTitle: '',
    imageSrc: '',
  };
};

/**
 * Filters out savings plan items from cart items, or formats them if shouldFilter is false
 */
export const useFilteredCartItems = <T extends PdProductItem>(
  items: T[],
  shouldFilter: boolean = true
): T[] => {
  const availablePlans = useSavingPlanStore((state) => state.availablePlans);

  return useMemo(() => {
    const savingsPlanVariantIds = availablePlans
      .map((plan) => String(plan.variantId))
      .filter(Boolean);

    const variantToPlanMap = new Map(
      availablePlans.map((plan) => [String(plan.variantId), plan.planName])
    );

    if (!shouldFilter) {
      return items.map((item) => {
        const itemVariantId = String(item.variantId);
        const planName = variantToPlanMap.get(itemVariantId);

        if (planName) {
          return formatSavingsPlanItem(item, planName);
        }

        return item;
      });
    }

    // Filter out savings plans
    return items.filter((item) => !savingsPlanVariantIds.includes(String(item.variantId)));
  }, [items, availablePlans, shouldFilter]);
};
